import React, { useState, useEffect } from 'react';
import axios from 'cccisd-axios';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import { isSupervisorViewing } from '../../helpers/helpers';
import Family from './Family';
import familyQuery from './families.graphql';
import familyQuerySupervisors from './families_supervisor.graphql';

const Boilerplate = window.cccisd.boilerplate;
const Fortress = window.cccisd.fortress;

function TfhCoachDashboard({ coachPawnId }) {
    let initialPinned = _get(Fortress, 'user.acting.role_data.pinnedFamilies', '');
    if (!initialPinned) {
        initialPinned = '{}';
    }

    if (typeof initialPinned !== 'object') {
        initialPinned = JSON.parse(initialPinned);
    }

    // PHP stores emtpy JSON as [] instead of {}
    if (Array.isArray(initialPinned)) {
        initialPinned = {};
    }

    const [families, setFamilies] = useState(null);
    const [pinnedFamilies, setPinnedFamilies] = useState(initialPinned);

    // keep Window in sync with database
    useEffect(() => {
        window.cccisd.fortress.user.acting.role_data.pinnedFamilies = pinnedFamilies;
    }, [pinnedFamilies]);

    useEffect(() => {
        async function getFamilies() {
            const resp = await client.query({
                query: isSupervisorViewing() ? familyQuerySupervisors : familyQuery,
                variables: isSupervisorViewing() ? { selectedCoachId: coachPawnId } : {},
                fetchPolicy: 'network-only',
            });
            const fams = _get(resp, 'data.groups.tfhClassList', []) || [];
            setFamilies(fams);
        }

        getFamilies();
    }, [coachPawnId]);

    function getFamilyName(family) {
        const label = _get(family, 'group.label');
        if (label) {
            return label;
        }

        const firstParent = _get(family, 'descendantRoles.parentList.0');
        if (firstParent) {
            return getMemberName(firstParent);
        }

        return 'Family ' + _get(family, 'group.groupId');
    }

    function getMemberName(member) {
        const { user } = member;
        if (!user) {
            return '';
        }

        const { fullName, firstName, lastName, username } = user;
        if (fullName) {
            return fullName;
        }
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }
        if (firstName) {
            return firstName;
        }

        return username;
    }

    function getIsPinned(family) {
        return family.group.groupId in pinnedFamilies && pinnedFamilies[family.group.groupId];
    }

    async function togglePinned(family) {
        const familyGroupId = _get(family, 'group.groupId');

        if (familyGroupId in pinnedFamilies && pinnedFamilies[familyGroupId]) {
            setPinnedFamilies(prev => {
                const next = { ...prev };
                delete next[familyGroupId];
                return next;
            });
        } else {
            setPinnedFamilies(prev => ({ ...prev, [familyGroupId]: true }));
        }

        const resp = await axios.patch(Boilerplate.route('api.app.updatePinnedFamilies'), { familyGroupId });
        if (resp.data.status !== 'success') {
            console.error('Yikes! Error trying to save pinned families: ', resp.data);
        }
    }

    if (!families || !Array.isArray(families)) {
        return <Loader loading />;
    }

    if (families.length === 0) {
        return (
            <div className="alert alert-info">
                <p className="mb-0">There are no active families to show.</p>
            </div>
        );
    }

    return (
        <div>
            {families
                .sort((a, b) => {
                    const isAPinned = getIsPinned(a);
                    const isBPinned = getIsPinned(b);
                    if (isAPinned === isBPinned) {
                        return getFamilyName(a).localeCompare(getFamilyName(b));
                    }

                    if (isAPinned) {
                        return -1;
                    }

                    return 1;
                })
                .map((family, i) => (
                    <Family
                        key={_get(family, 'group.groupId', 'fam' + i)}
                        family={family}
                        familyName={getFamilyName(family)}
                        isPinned={getIsPinned(family)}
                        togglePinned={togglePinned}
                        getMemberName={getMemberName}
                    />
                ))}
        </div>
    );
}

export default TfhCoachDashboard;
